import Editor, { useMonaco } from "@monaco-editor/react";
import React, { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import Consultant from "@/models/Consultant";
import { PreviewPersonFileTemplateResponse } from "@/models/PersonFileTemplate";
import {
	personFileTemplateErrorMessage,
	usePersonFileTemplate,
	usePersonFileTemplateTypes,
	usePreviewPersonFileTemplate,
	useResetPersonFileTemplate,
	useSavePersonFileTemplate,
} from "@/networking/hogia.hooks";
import { configureScriptEditorTypescript, setScriptContextTypes } from "@/utils/monaco.utils";
import { toast } from "@/utils/toast";
import { SettingsLeaf } from "../SettingsLeafLayout";
import { primaryButtonClasses, secondaryButtonClasses } from "../TaxonomyCrudEditor";
import PersonFilePreview, { WarningPanel } from "./PersonFilePreview";

const PREVIEW_DEBOUNCE_MS = 600;

/**
 * Debounced run of the draft script for the selected consultant. Responses that
 * arrive out of order are dropped so the output always matches the latest
 * input. Note that a script that fails to compile or throws still comes back as
 * a successful response with `error` set — only transport/server failures land
 * in `requestError`.
 */
function useDebouncedPreview(source: string | undefined, consultantId: string | undefined) {
	const { mutateAsync } = usePreviewPersonFileTemplate();
	const [data, setData] = useState<PreviewPersonFileTemplateResponse>();
	const [loading, setLoading] = useState(false);
	const [requestError, setRequestError] = useState<string>();
	const seq = useRef(0);

	useEffect(() => {
		if (!consultantId || source === undefined) {
			setLoading(false);
			return;
		}
		const mySeq = ++seq.current;
		setLoading(true);
		const timer = setTimeout(() => {
			mutateAsync({ consultantId, source })
				.then((res) => {
					if (mySeq !== seq.current) return;
					setData(res);
					setRequestError(undefined);
				})
				.catch((e) => {
					if (mySeq !== seq.current) return;
					setRequestError(personFileTemplateErrorMessage(e, "Förhandsgranskningen misslyckades"));
				})
				.finally(() => {
					if (mySeq === seq.current) setLoading(false);
				});
		}, PREVIEW_DEBOUNCE_MS);

		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [source, consultantId]);

	return { data, loading, requestError };
}

/**
 * "Personalfil" — the generator script for each consultant's section of the
 * Hogia person file. Exactly one script per organisation, so the editor is the
 * settings leaf itself (no list, no sub-route). Backed by oo-hogia-hr-service
 * `/hogia/person-file-template`; the same script model as the invoice templates
 * (edited in oo-admin).
 *
 * Only shown for organisations that export salary to Hogia — see the `visible`
 * predicate on the leaf in `settingsNav.tsx`.
 */
const PersonalfilPage: React.FC = () => {
	const { template, isLoading, refetch } = usePersonFileTemplate();
	const { types } = usePersonFileTemplateTypes();
	const { mutateAsync: saveTemplate, isLoading: saving } = useSavePersonFileTemplate();
	const { mutateAsync: resetTemplate, isLoading: resetting } = useResetPersonFileTemplate();

	const [draft, setDraft] = useState<string>();
	const [consultant, setConsultant] = useState<Consultant>();
	const [saveError, setSaveError] = useState<string>();
	const [saveWarnings, setSaveWarnings] = useState<string[]>([]);
	const [confirmingReset, setConfirmingReset] = useState(false);

	// Initialise the draft from the stored script (or the built-in default).
	useEffect(() => {
		if (draft === undefined && template) setDraft(template.source);
	}, [draft, template]);

	// Monaco: TypeScript defaults + the served `PersonFileContext` definitions as
	// a global lib, so the author gets type checking, autocomplete and the
	// Swedish hover docs. `useMonaco()` resolves once the (CDN-loaded) instance
	// exists, so this runs whenever either the instance or the types arrive — in
	// any order. (`onMount` is not usable for this: the Editor captures it on its
	// first render, so it would see a stale `types`.) The lib gets its own
	// filePath so it can't collide with the invoice editor's context types.
	const monaco = useMonaco();
	useEffect(() => {
		if (!monaco) return;
		configureScriptEditorTypescript(monaco);
		if (types) setScriptContextTypes(monaco, "file:///person-file-context.d.ts", types);
	}, [monaco, types]);

	const preview = useDebouncedPreview(draft, consultant?.id);

	const dirty = !!template && draft !== undefined && draft !== template.source;
	/**
	 * A draft that doesn't compile or throws can't be saved — the backend rejects
	 * it anyway. Monaco also flags type errors inline, but the preview's `error`
	 * is the authoritative one: it comes from the engine that actually runs the
	 * script.
	 */
	const hasCompileError = !!preview.data?.error;

	const save = async () => {
		if (draft === undefined) return;
		setSaveError(undefined);
		try {
			const { warnings } = await saveTemplate({ source: draft });
			const { data } = await refetch();
			if (data) setDraft(data.source);
			setSaveWarnings(warnings ?? []);
			toast.success("Personalfilmallen sparad");
		} catch (e: any) {
			setSaveError(personFileTemplateErrorMessage(e, "Mallen kunde inte sparas"));
		}
	};

	const reset = async () => {
		setSaveError(undefined);
		try {
			await resetTemplate();
			const { data } = await refetch();
			if (data) setDraft(data.source);
			setSaveWarnings([]);
			setConfirmingReset(false);
			toast.success("Standardmallen återställd");
		} catch (e: any) {
			setConfirmingReset(false);
			setSaveError(personFileTemplateErrorMessage(e, "Mallen kunde inte återställas"));
		}
	};

	return (
		<SettingsLeaf
			full
			title="Personalfil"
			description={
				<>
					Mallen är ett TypeScript-skript där <code className="font-mono">generate(ctx)</code> får
					konsultens uppgifter och returnerar konsultens avsnitt i personalfilen till Hogia,
					inklusive raderna <code className="font-mono">Pstart</code> och{" "}
					<code className="font-mono">PSlut</code>.<br />
					<br />
					OBS! Endast tecken som ryms inom <strong>ISO-8859-1</strong> får användas.
				</>
			}
		>
			{isLoading || draft === undefined || !template ? (
				<div className="flex items-center gap-2 text-sm text-gray-500">
					<Loader2 size={16} className="animate-spin" /> Hämtar mallen…
				</div>
			) : (
				<>
					<div className="flex flex-wrap items-center gap-3">
						<span
							className={cn(
								"rounded px-2 py-0.5 text-[11px] font-semibold",
								template.isDefault ? "bg-gray-100 text-gray-600" : "bg-blue-50 text-blue-700"
							)}
						>
							{template.isDefault ? "Origos standardmall" : "Anpassad mall"}
						</span>
						<span className={cn("text-xs", dirty ? "text-amber-600" : "text-gray-400")}>
							{dirty ? "Osparade ändringar" : "Inga ändringar"}
						</span>

						<div className="flex-1" />

						{!template.isDefault && (
							<button
								type="button"
								className={cn(secondaryButtonClasses, "flex items-center gap-1.5")}
								onClick={() => setConfirmingReset(true)}
								disabled={resetting}
							>
								<RotateCcw size={14} /> Återställ till standard
							</button>
						)}
						<button
							type="button"
							className={primaryButtonClasses}
							disabled={!dirty || saving || hasCompileError}
							onClick={save}
							title={
								hasCompileError
									? "Skriptet går inte att köra — se felet i förhandsgranskningen"
									: undefined
							}
						>
							{saving ? "Sparar…" : "Spara"}
						</button>
					</div>

					{saveError && (
						<div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
							{saveError}
						</div>
					)}
					<WarningPanel warnings={saveWarnings} />

					<div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
						<section className="space-y-2">
							<h3 className="text-sm font-semibold text-gray-700">Skript</h3>
							<div className="overflow-hidden rounded-md border border-gray-200 bg-white">
								<Editor
									height="calc(100vh - 24rem)"
									defaultLanguage="typescript"
									path="file:///person-file-generator.ts"
									value={draft}
									onChange={(value) => setDraft(value ?? "")}
									options={{
										minimap: { enabled: false },
										fontSize: 13,
										tabSize: 2,
										scrollBeyondLastLine: false,
										wordWrap: "on",
										automaticLayout: true,
									}}
									loading={
										<div className="flex h-40 items-center justify-center gap-2 text-xs text-gray-400">
											<Loader2 size={14} className="animate-spin" /> Laddar editorn…
										</div>
									}
								/>
							</div>
						</section>

						<section className="space-y-2 xl:sticky xl:top-4 xl:self-start">
							<h3 className="text-sm font-semibold text-gray-700">Så här blir filen</h3>
							<PersonFilePreview
								consultant={consultant}
								onConsultantChange={setConsultant}
								data={preview.data}
								loading={preview.loading}
								requestError={preview.requestError}
							/>
						</section>
					</div>

					<AlertDialog open={confirmingReset} onOpenChange={setConfirmingReset}>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Återställ till standardmallen?</AlertDialogTitle>
								<AlertDialogDescription>
									Organisationens egna mall tas bort och personalfilen skapas åter med Origos
									standardmall. Det går inte att ångra.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel disabled={resetting}>Avbryt</AlertDialogCancel>
								<AlertDialogAction
									onClick={(e) => {
										e.preventDefault();
										reset();
									}}
									disabled={resetting}
								>
									{resetting ? "Återställer…" : "Återställ"}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</>
			)}
		</SettingsLeaf>
	);
};

export default PersonalfilPage;
